import { CustomError, logAndCaptureException } from '../../utils/errorTools'

/**
 * The Ketch Consent Management Platform tag, loaded from their CDN (see `gatsby-ssr.js`). Declared
 * here beside the code that drives it, the way `lib/cart/apple-pay.ts` declares `ApplePaySession`.
 * Keeping it out of `types/globals` means a test can stand the tag in: `tsconfig.spec.json` doesn't
 * include that directory, and adding it drags in the module declarations beside it.
 */
declare global {
  interface Window {
    ketch?: {
      (type: 'showExperience' | 'showConsent' | 'showPreferences'): void
      (type: 'setIdentities', identities: Record<string, unknown>): void
      // `willShowExperience` passes the experience type ('experiences.consent' /
      // 'experiences.preference'); the display type comes from the config, not the event
      (
        type: 'on' | 'off',
        event: 'willShowExperience' | 'hideExperience',
        callback: (...args: unknown[]) => void,
      ): void
      (type: 'getConfig', callback: (value: unknown) => void): void
      // the visitor's region from Ketch's IP lookup, as `country-region` (`US-CA`)
      (type: 'getRegionInfo', callback: (value: unknown) => void): void
      // NOTE: internal function - not part of public API
      (type: 'getIsDisplayed', callback: (value: unknown) => void): void
    }
  }
}

/** Error indicating an assumption about Ketch's internal API was violated  */
export class KetchInternalApiError extends CustomError {
  override name = 'KetchInternalApiError'
}

export const mkGetIsDisplayedCallback =
  (callback: (isDisplayed: boolean) => void): ((value: unknown) => void) =>
  (rawValue) => {
    if (typeof rawValue !== 'boolean') {
      logAndCaptureException(
        new KetchInternalApiError(
          `Invalid getIsDisplayed callback value type (expected: boolean, actual: ${typeof rawValue})`,
        ),
      )
    } else {
      callback(rawValue)
    }
  }

/**
 * The experience type `willShowExperience` reports. The preference centre is always a full-page
 * overlay, so this alone answers whether it covers the page: no config read, and no dependence on
 * `autoInitiated.content.display` tracking a user-initiated experience. The consent experience is
 * the one that still needs the display type, since a property configures it as either.
 */
export const KETCH_PREFERENCE_EXPERIENCE = 'experiences.preference'

export const isKetchPreferenceExperience = (experienceType: unknown): boolean =>
  experienceType === KETCH_PREFERENCE_EXPERIENCE

/**
 * How Ketch renders an experience. Only `banner` leaves the page usable: `modal` and `preference`
 * are both full-page overlays.
 */
export const ketchExperienceDisplays = ['banner', 'modal', 'preference'] as const

export type KetchExperienceDisplay = (typeof ketchExperienceDisplays)[number]

const isKetchExperienceDisplay = (value: unknown): value is KetchExperienceDisplay =>
  ketchExperienceDisplays.some((display) => display === value)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/**
 * The display type of the experience Ketch has on screen, from
 * `experiences.autoInitiated.content.display` in its config. The key reads oddly for a
 * user-initiated experience, but Ketch keeps this one value in step with whatever is showing, which
 * is why `state.ts` reads it only for a consent experience and keeps the first answer. A
 * `userInitiated` branch beside it carries the same shape; look there first if a consent display
 * ever reads wrong.
 */
const readExperienceDisplay = (config: unknown): unknown => {
  if (!isRecord(config)) {
    return undefined
  }
  const { experiences } = config
  if (!isRecord(experiences)) {
    return undefined
  }
  const { autoInitiated } = experiences
  if (!isRecord(autoInitiated)) {
    return undefined
  }
  const { content } = autoInitiated
  return isRecord(content) ? content.display : undefined
}

/**
 * Callback for `ketch('getConfig', …)` that hands the caller the current experience's display type,
 * or `undefined` where the config didn't carry a recognized one. Reporting an unknown value matters
 * more than it looks: callers treat `undefined` as covering the page, so a display type Ketch adds
 * later degrades to the old always-suppress behaviour instead of silently letting a modal render
 * under an overlay.
 */
export const mkGetConfigDisplayCallback =
  (callback: (display: KetchExperienceDisplay | undefined) => void): ((value: unknown) => void) =>
  (rawConfig) => {
    const display = readExperienceDisplay(rawConfig)
    if (!isKetchExperienceDisplay(display)) {
      logAndCaptureException(
        new KetchInternalApiError(
          `Invalid getConfig experience display (expected: ${ketchExperienceDisplays.join(
            ' | ',
          )}, actual: ${JSON.stringify(display)})`,
        ),
      )
      callback(undefined)
    } else {
      callback(display)
    }
  }
